// src/FormItem.tsx
import { createContext as createContext3, createEffect, createMemo, onCleanup, splitProps, useContext as useContext2 } from "solid-js";
import { Dynamic } from "solid-js/web";

// src/InputLabel.tsx
import { Show } from "solid-js";
var InputLabel = (props) => {
  return <div>
      <div class="mr-1">
        {props.label}
        <Show when={props.required}>
          <span class="text-error">*</span>
        </Show>
      </div>
      <Show when={props.description}>
        <div class="text-warning text-xs normal-case">{props.description}</div>
      </Show>
    </div>;
};

// src/context.ts
import { createContext, useContext } from "solid-js";
import { createStore, produce, unwrap } from "solid-js/store";

// src/utils.ts
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import Decimal from "decimal.js";
var emailRegex = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
var isPlainObject = (value) => {
  return typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype;
};
function cleanData(data) {
  if (Array.isArray(data)) {
    const cleanedArray = data.map((item) => cleanData(item)).filter((item) => !isFalsy(item));
    return cleanedArray.length > 0 ? cleanedArray : void 0;
  } else if (isPlainObject(data)) {
    const cleanedObject = Object.entries(data).reduce((acc, [key, value]) => {
      const cleanedValue = cleanData(value);
      if (!isFalsy(cleanedValue)) {
        acc[key] = cleanedValue;
      }
      return acc;
    }, {});
    return Object.keys(cleanedObject).length > 0 ? cleanedObject : void 0;
  }
  return data;
}
var isRequired = (rules = []) => {
  const ruleIsRequired = rules.find((rule) => rule.required);
  if (ruleIsRequired) return ruleIsRequired;
  return false;
};
var isControlledByYourself = (rules = []) => {
  return rules.some((rule) => rule.type === "controlledByYourself");
};
var isFalsy = (value) => {
  return value === void 0 || value === null || value === "";
};
var getErrorMessage = (value, rules = []) => {
  const required = isRequired(rules);
  function getResponse(message, isError) {
    return {
      isError,
      message
    };
  }
  if (required && isFalsy(value)) {
    return getResponse(required.message || "", true);
  }
  for (const rule of rules) {
    if (rule.validator) {
      const res = rule.validator(value);
      if (res === true || res === "") {
        continue;
      }
      return getResponse(typeof res === "string" ? res : rule.message, true);
    }
    if (!isFalsy(value)) {
      if (rule.regex && !rule.regex.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "name" && !/^[\p{L}\p{M}\s'-]{1,150}$/u.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "tenantName" && !/^[A-Za-z\s',.-]{2,150}$/u.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "email" && !emailRegex.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "phone" && !/^\+1\s*(?:\(?\d{3}\)?[\s-]?)?\d{3}[\s-]?\d{4}$|^\d{10}$|^\d{3}[\s-]\d{3}[\s-]\d{4}$|^\(\d{3}\)\s?\d{3}[\s-]\d{4}$/.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "number") {
        if (isNaN(value)) {
          return getResponse(rule.message, true);
        }
      }
      if (rule.type === "int") {
        if (!Number.isInteger(value)) {
          return getResponse(rule.message, true);
        }
      }
      if (rule.type === "numberInputStep") {
        if (isNaN(value)) {
          return getResponse(rule.message, true);
        }
        if (!rule.numberInputStep) {
          return getResponse("numberInputStep is required when rule type is numberInputStep", true);
        }
        const valueDecimal = new Decimal(value);
        if (!valueDecimal.mod(rule.numberInputStep).eq(0)) {
          return getResponse(rule.message, true);
        }
      }
      if (rule.type === "dateOfBirth") {
        const isDateOfBirthInvalid = dayjs().isBefore(dayjs(value)) || !/^\d{4}-\d{2}-\d{2}$/.test(value);
        const isUnder18YearsAnd2Days = dayjs().isBefore(dayjs(value).add(18, "year").add(2, "day"));
        const isOver125Years = dayjs().isAfter(dayjs(value).add(125, "year"));
        if (isDateOfBirthInvalid || isUnder18YearsAnd2Days || isOver125Years) {
          return getResponse(rule.message, true);
        }
      }
      if (rule.type === "ssn" && !/^(\d{3}-{0,1}\d{2}-{0,1}\d{4}|\d{9}|\d{2}-{0,1}\d{7})$/.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "ein" && !/^\d{2}-?\d{7}$/.test(value)) {
        return getResponse(rule.message, true);
      }
      if (rule.length && typeof rule.length === "number" && value.length > rule.length) {
        return getResponse(rule.message, true);
      }
      if (rule.length && Array.isArray(rule.length) && (value.length < rule.length[0] || value.length > rule.length[1])) {
        return getResponse(rule.message, true);
      }
      if (rule.range && (value < rule.range[0] || value > rule.range[1])) {
        return getResponse(rule.message, true);
      }
      if (rule.type === "date") {
        if (!dayjs(value).isValid()) {
          return getResponse(rule.message, true);
        }
        if (rule.dateRange && (dayjs(value).isBefore(dayjs(rule.dateRange[0])) || dayjs(value).isAfter(dayjs(rule.dateRange[1])))) {
          return getResponse(rule.message, true);
        }
      }
    }
  }
  return getResponse("", false);
};
var transFormNamePath = (name = []) => {
  return Array.isArray(name) ? name : stringToNamePath(name);
};
var namePathToString = (namePath) => {
  return transFormNamePath(namePath).join(".");
};
var stringToNamePath = (namePathStr) => {
  return namePathStr.split(".").map((key) => /^\d+$/.test(key) ? Number(key) : key);
};
var compareTwoNamePath = (namePath1, namePath2) => {
  const namePath1Arr = transFormNamePath(namePath1);
  const namePath2Arr = transFormNamePath(namePath2);
  if (namePath1Arr.length !== namePath2Arr.length) return false;
  for (let i = 0; i < namePath1Arr.length; i++) {
    if (namePath1Arr[i] !== namePath2Arr[i]) return false;
  }
  return true;
};
var combineNamePaths = (...namePaths) => {
  return namePaths.reduce((acc, cur) => {
    return acc.concat(transFormNamePath(cur));
  }, []);
};
var FieldDataKeyName = "data-form-field-name";
var FormDataKeyName = "data-form-name";

// src/scroll.ts
var scrollToFormField = (formName, errorFields) => {
  for (const field of errorFields) {
    const name = field.name;
    if (!name) continue;
    const formField = document.querySelector(`[${FormDataKeyName}="${formName}"]`)?.querySelector(`[${FieldDataKeyName}="${namePathToString(name)}"]`);
    if (formField) {
      formField.scrollIntoView({ behavior: "smooth", block: "center" });
      break;
    }
  }
};

// src/context.ts
import { cloneDeep } from "lodash-es";
var defaultFormContext = createContext();
var getFormContext = () => {
  return defaultFormContext;
};
var useFormContext = (formContext) => {
  const context = useContext(defaultFormContext || formContext);
  if (!context) {
    throw new Error("Form context is not found");
  }
  return context;
};
var getValueFromStore = (store, name) => {
  if (!name) {
    return;
  }
  const _namePath = transFormNamePath(name);
  return _namePath.reduce((acc, cur) => acc?.[cur], store);
};
var copyStore = (store) => {
  return cloneDeep(store);
};
var useForm = (config) => {
  const [configStore, setConfigStore] = createStore({
    fieldsErrors: {},
    rules: {},
    validateTriggers: config?.validateTriggers,
    onFieldChange: config?.onFieldChange,
    inputLabel: config?.inputLabel,
    formName: `form-${Math.random().toString(36).substring(2, 15)}`
  });
  const [formStore, setFormStore] = createStore(config?.initialValues || {});
  function setFormError(namePath, error) {
    const namePathStr = transFormNamePath(namePath);
    if (namePathStr.length > 0) {
      setConfigStore(
        produce((draft) => {
          const path = namePathStr.join(".");
          error ? draft.fieldsErrors[path] = error : delete draft.fieldsErrors[path];
        })
      );
    }
  }
  function validateForm(name, setError = true) {
    const { rules, fieldsErrors } = unwrap(configStore);
    const _formStore = unwrap(formStore);
    const result = {
      errorFields: [],
      validateStatus: true
    };
    for (const key in rules) {
      const namePath = stringToNamePath(key);
      if (name && !compareTwoNamePath(namePath, name)) continue;
      const value = getValueFromStore(_formStore, namePath);
      const rule = rules[key];
      if (isControlledByYourself(rule)) {
        if (fieldsErrors[key]) {
          result.validateStatus = false;
          result.errorFields?.push({
            name: namePath
          });
        }
        continue;
      }
      const { isError, message } = getErrorMessage(value, rule);
      if (isError) {
        result.validateStatus = false;
        result.errorFields?.push({
          name: namePath,
          message
        });
        if (setError || !!getFormFieldError(namePath)) setFormError(namePath, message);
      } else {
        setFormError(namePath, "");
      }
    }
    return result;
  }
  function validateFormAndScrollToFirstError() {
    const validateResult = validateForm();
    if (validateResult.errorFields.length > 0) {
      scrollToFormField(configStore.formName, validateResult.errorFields);
    }
    return validateResult;
  }
  function setFormFieldRules(rules, namePath) {
    const namePathStr = transFormNamePath(namePath);
    if (namePathStr.length > 0) {
      setConfigStore("rules", (_rules) => ({
        ..._rules,
        [namePathStr.join(".")]: rules
      }));
    }
  }
  function getFormFieldError(namePath) {
    const namePathStr = transFormNamePath(namePath);
    if (namePathStr.length > 0) {
      return configStore.fieldsErrors[namePathStr.join(".")];
    }
  }
  function setFieldValue(name, value, needClean = true) {
    if (!name) return;
    const { validateTriggers } = unwrap(configStore);
    const cleanedValue = needClean ? cleanData(value) : value;
    const _namePath = transFormNamePath(name);
    setFormStore(
      produce((draft) => {
        _namePath.reduce((acc, cur, index) => {
          if (index === _namePath.length - 1) {
            if (cleanedValue !== void 0) {
              acc[cur] = cleanedValue;
            } else {
              delete acc[cur];
            }
          } else {
            const defaultValue = Number.isNaN(+_namePath[index + 1]) ? {} : [];
            acc[cur] = acc[cur] || defaultValue;
          }
          return acc[cur];
        }, draft);
      })
    );
    validateForm(name, !!validateTriggers?.includes("onChange"));
  }
  function setFieldsValue(values, needClean = true) {
    const meaningfulValues = needClean ? cleanData(values) : values;
    const { validateTriggers } = unwrap(configStore);
    if (formStore) {
      setFormStore(cloneDeep(meaningfulValues));
      validateForm(void 0, !!validateTriggers?.includes("onChange"));
    }
  }
  function getFieldValue(name) {
    return getValueFromStore(formStore, name);
  }
  function removeFormField(name, opts = {}) {
    setConfigStore(
      produce((draft) => {
        delete draft.rules[namePathToString(name)];
        delete draft.fieldsErrors[namePathToString(name)];
      })
    );
    if (opts.removeValue) {
      setFormStore(
        produce((draft) => {
          const _namePath = transFormNamePath(name);
          _namePath.reduce((acc, cur, index) => {
            if (index === _namePath.length - 1) {
              delete acc[cur];
            }
            return acc[cur];
          }, draft);
        })
      );
    }
  }
  function resetFields(namePaths) {
    if (!namePaths) {
      setFormStore(
        produce((draft) => {
          Object.keys(draft).forEach((key) => {
            draft[key] = void 0;
          });
        })
      );
      return;
    }
    namePaths.forEach((cur) => {
      setFieldValue(cur, void 0);
    });
  }
  const clearValidate = (names) => {
    setConfigStore(
      produce((draft) => {
        if (names?.length) {
          names.forEach((name) => {
            const path = namePathToString(name);
            delete draft.fieldsErrors[path];
          });
        } else {
          draft.fieldsErrors = {};
        }
      })
    );
  };
  return {
    get isValidate() {
      return Object.values(configStore.fieldsErrors || {}).every((cur) => !cur);
    },
    formStore,
    setFormStore,
    configStore,
    setConfigStore,
    setFieldValue,
    setFieldsValue,
    getFieldValue,
    resetFields,
    clearValidate,
    setFormError,
    setFormFieldRules,
    getFormFieldError,
    validateForm: validateFormAndScrollToFirstError,
    removeFormField
  };
};

// src/formListContext.ts
import { createContext as createContext2 } from "solid-js";
var FormListContext = createContext2();

// src/FormItem.tsx
var defaultOnChangeMethodName = "onInput";
var defaultGetValueFromEvent = (val) => val;
var formItemContext = createContext3();
var useFormItemContext = () => {
  return useContext2(formItemContext);
};
var FormItem = function(props) {
  const formContext = useFormContext();
  const formListContext = useContext2(FormListContext);
  const [formItemProps, componentProps] = splitProps(props, [
    "component",
    "formFieldName",
    "onChangeMethodName",
    "label",
    "rules",
    "removeValueOnClean",
    "customSyncValue",
    "customGetValue",
    "getValueFromEvent",
    "valuePropName",
    "description"
  ]);
  if (!formContext) throw new Error("FormItem must be used within a FormWrapper");
  const realFormFieldName = createMemo(() => {
    if (formListContext && formItemProps.formFieldName) {
      return combineNamePaths(formListContext.formFieldName, [formListContext.index], formItemProps.formFieldName);
    }
    return formItemProps.formFieldName;
  });
  const labelJSX = createMemo(
    () => formItemProps.label ? <Dynamic
      component={formContext.configStore.inputLabel || InputLabel}
      description={formItemProps.description}
      label={formItemProps.label}
      required={!!isRequired(formItemProps.rules)}
    /> : null
  );
  const onChangeMethodName = createMemo(() => formItemProps.onChangeMethodName || defaultOnChangeMethodName);
  const value = createMemo(() => {
    if (typeof formItemProps.customGetValue === "function") {
      return formItemProps.customGetValue(formContext);
    }
    return getValueFromStore(formContext.formStore, realFormFieldName());
  });
  const getValueFromEvent = createMemo(() => {
    if (formItemProps.getValueFromEvent) return formItemProps.getValueFromEvent;
    return defaultGetValueFromEvent;
  });
  const autoBindProps = createMemo(() => {
    return {
      [formItemProps.valuePropName || "value"]: value(),
      [onChangeMethodName()]: (...args) => {
        const value2 = args[0];
        const isEmpty2 = isFalsy(value2) || Array.isArray(value2) && value2.length === 0 || isPlainObject(value2) && Object.keys(value2).length === 0;
        const realValue = isEmpty2 ? void 0 : getValueFromEvent()(value2);
        if (formItemProps.customSyncValue) {
          formItemProps.customSyncValue(formContext, realValue);
        } else {
          formContext.setFieldValue(realFormFieldName(), realValue);
        }
        componentProps[onChangeMethodName()]?.(...args);
        formContext.configStore.onFieldChange?.();
      },
      error: formContext.getFormFieldError(realFormFieldName())
    };
  });
  createEffect(() => {
    const _realFormFieldName = realFormFieldName();
    if (formItemProps.rules && _realFormFieldName) {
      formContext.setFormFieldRules(formItemProps.rules, _realFormFieldName);
    }
  });
  onCleanup(() => {
    const _realFormFieldName = realFormFieldName();
    if (_realFormFieldName) {
      formContext.removeFormField(_realFormFieldName);
    }
    if (formItemProps.removeValueOnClean && _realFormFieldName) {
      formContext.removeFormField(_realFormFieldName, { removeValue: true });
    }
  });
  return <formItemContext.Provider value={{ formFieldName: realFormFieldName }}>
      <Dynamic
    {...componentProps}
    {...autoBindProps()}
    component={formItemProps.component}
    label={props.label}
    labelJSX={labelJSX()}
  />
    </formItemContext.Provider>;
};

// src/FormList.tsx
import { createEffect as createEffect2, createMemo as createMemo3, For, Show as Show3, untrack } from "solid-js";

// src/FormFieldError.tsx
import { createMemo as createMemo2, Show as Show2 } from "solid-js";
var FormFieldError = (props) => {
  const formIns = useFormContext();
  const formItemContext2 = useFormItemContext();
  const formFieldName = createMemo2(() => {
    return props.formFieldName || formItemContext2?.formFieldName();
  });
  const error = createMemo2(() => {
    return formIns.getFormFieldError(formFieldName());
  });
  return <Show2 when={error()}>
      <div class="mt-2 text-left text-xs normal-case text-red-500">
        <span>{error()}</span>
      </div>
    </Show2>;
};

// src/FormList.tsx
var FormList = (props) => {
  const formContext = useFormContext();
  if (!formContext) throw new Error("FormList must be used within a FormWrapper");
  const val = createMemo3(() => getValueFromStore(formContext.formStore, props.formFieldName) || []);
  const onDelete = (index) => {
    const arr = copyStore(val());
    if (index >= arr.length) return;
    arr.splice(index, 1);
    formContext.setFieldValue(props.formFieldName, arr, false);
  };
  const itemsGroups = createMemo3(() => {
    if (!props.renderItemsGroup) return null;
    return <For each={val()}>
        {(item, index) => <FormListContext.Provider
      value={{
        formFieldName: props.formFieldName,
        autoCombineFieldName: props.autoCombineFieldName || true,
        index: untrack(() => index())
      }}
    >
            {props.renderItemsGroup?.({
      remove: onDelete.bind(null, index()),
      item,
      index
    })}
          </FormListContext.Provider>}
      </For>;
  });
  const children = createMemo3(() => {
    if (!props.children) return null;
    return props.children({
      val,
      add(initValue) {
        const arr = copyStore(val());
        arr.push(initValue || {});
        formContext.setFieldValue(props.formFieldName, arr, false);
      },
      delete: onDelete,
      prefix: transFormNamePath(props.formFieldName)
    });
  });
  createEffect2(() => {
    const { rules, formFieldName } = props;
    if (rules?.length && formFieldName) {
      formContext.setFormFieldRules(rules, formFieldName);
    }
  });
  return <div class={props.class}>
      <div class={props.itemsGroupsClass}>{itemsGroups()}</div>
      {children()}

      <Show3 when={props.rules?.length}>
        <FormFieldError formFieldName={props.formFieldName} />
      </Show3>
    </div>;
};

// src/Form.tsx
import { createEffect as createEffect3, onMount, splitProps as splitProps2 } from "solid-js";
var Form = (_props) => {
  const [localProps, formProps] = splitProps2(_props, [
    "onFormSubmit",
    "children",
    "initialValues",
    "defaultForm",
    "validateTriggers",
    "onFieldChange"
  ]);
  const FormContext = getFormContext();
  const defaultForm = localProps.defaultForm || useForm();
  onMount(() => {
    defaultForm.setFieldsValue(localProps.initialValues);
  });
  createEffect3(() => {
    const _config = {};
    if (localProps.validateTriggers) {
      _config.validateTriggers = localProps.validateTriggers;
    }
    if (localProps.onFieldChange) {
      _config.onFieldChange = localProps.onFieldChange;
    }
    defaultForm.setConfigStore(_config);
  });
  return <FormContext.Provider value={defaultForm}>
      <form
    {...Object.assign({}, formProps, {
      [FormDataKeyName]: defaultForm.configStore.formName
    })}
    noValidate
    onSubmit={(e) => {
      e.preventDefault();
      e.stopPropagation();
      const { validateStatus } = defaultForm.validateForm();
      if (validateStatus) {
        localProps.onFormSubmit?.(defaultForm.formStore);
      }
    }}
  >
        {localProps.children}
      </form>
    </FormContext.Provider>;
};

// src/FormField.tsx
import { createMemo as createMemo4, splitProps as splitProps3 } from "solid-js";
import { Dynamic as Dynamic2 } from "solid-js/web";
var FormField = function(props) {
  const formItemContext2 = useFormItemContext();
  const [local, restProps] = splitProps3(props, ["as"]);
  const fieldProps = createMemo4(() => {
    if (!formItemContext2) return {};
    return {
      [FieldDataKeyName]: namePathToString(formItemContext2.formFieldName())
    };
  });
  return <Dynamic2 component={local.as || "div"} {...restProps} {...fieldProps()} />;
};
export {
  Form,
  FormField,
  FormFieldError,
  FormItem,
  FormList,
  scrollToFormField,
  useForm,
  useFormContext,
  useFormItemContext
};
